import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import { LineChart, AnnotatableChart, CombinedBarLineChart } from '../../../../../shared/components/charts';
import { DashboardCard } from '../../../../../shared/components/DashboardCard';
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
  ComparisonToggle,
  GranularityToggle,
  ComparisonColumns,
  WeatherAnomalyCallout,
} from '../../../../../shared/components';
import { useSessionQuery } from '../../../../../features/auth/hooks';
import type { PatternDataPoint } from '../../../../../shared/api/types';
import { useClientScope } from '../../../../../shared/contexts';
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
  useComparisonMode,
  useComparisonGroups,
  useComparisonTrends,
  useComparisonPatterns,
} from '../../../../../shared/hooks';
import { AiSummaryPanel, useAiSummary } from '../../../insights';
import { useStrainActivityTrends, useStrainPatterns, useRecoveryPatterns } from '../hooks';
import { StrainActivityStatsColumn } from '../components/StrainActivityStatsColumn';
import { buildFilterParams, transformTrendsData, useResolvedGranularity } from '../../shared/utils';
import { buildComparisonTrendSeries, buildComparisonPatternSeries } from '../../../../../shared/utils';

const STRAIN_AND_ACTIVITY_DETAILS = {
  strainAndActivity: `Displays daily fluctuations in average strain and activity levels across the organisation, illustrating patterns of physical exertion over time. Strain reflects overall cardiovascular load and intensity of effort, while activity represents movement volume and duration.

Together, these trends provide insight into how consistently employees are engaging in physical activity, periods of elevated exertion, and how overall workload may influence recovery balance. Monitoring these patterns helps identify sustained high-load periods, potential fatigue risk, and broader shifts in organisational activity behaviour.`,
  heartRateZones: `Shows the distribution of time spent in different heart rate intensity zones, from low to high effort. This reflects overall training load and how exercise intensity is managed.

By analysing time spent in each zone, it provides insight into cardiovascular demand, effort distribution, and whether activity levels are predominantly moderate, vigorous, or high intensity.`,
  weeklyStrainPatterns: `Compares daily strain and recovery percentages across the week to assess workload distribution and recovery balance. Strain represents cumulative cardiovascular and physiological load, while recovery reflects the body’s readiness and restorative status.

By examining these patterns across weekdays and weekends, this view highlights periods of sustained high exertion, insufficient recovery, or improved balance. It supports identification of workload clustering, potential fatigue risk, and opportunities to optimise recovery timing to maintain performance and wellbeing throughout the week.`,
};

export const StrainAndActivity: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);
  const [trendView, setTrendView] = useState('day_strain');

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId, null, null, { isCentralAdmin }),
    [filterState, dateRange, effectiveClientId, isCentralAdmin],
  );

  // Client events: central admins and client admins may annotate; the popover
  // is read-only for everyone else. Only enabled for a single selected client.
  const { data: session } = useSessionQuery();
  const canEditEvents = Boolean(isCentralAdmin || session?.is_admin);
  const [startDate, endDate] = dateRange;
  const eventStartDate = startDate ? startDate.format('YYYY-MM-DD') : undefined;
  const eventEndDate = endDate ? endDate.format('YYYY-MM-DD') : undefined;

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      day_strain: {
        min: typeof metric_min.day_strain === 'number' ? metric_min.day_strain : undefined,
        max: typeof metric_max.day_strain === 'number' ? metric_max.day_strain : undefined,
      },
      cardio_load: {
        min: typeof metric_min.cardio_load === 'number' ? metric_min.cardio_load : undefined,
        max: typeof metric_max.cardio_load === 'number' ? metric_max.cardio_load : undefined,
      },
      heart_rate: {
        min: typeof metric_min.heart_rate === 'number' ? metric_min.heart_rate : undefined,
        max: typeof metric_max.heart_rate === 'number' ? metric_max.heart_rate : undefined,
      },
      calories_burn: {
        min: typeof metric_min.calories_burn === 'number' ? metric_min.calories_burn : undefined,
        max: typeof metric_max.calories_burn === 'number' ? metric_max.calories_burn : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  // Comparison mode: render one column/line per selected filter value.
  const comparison = useComparisonMode(filterState);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filterState,
    dateRange,
    effectiveClientId,
    comparison,
    isCentralAdmin,
  );

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Strain & Activity',
    groups,
    isComparing,
    dimension: comparison.dimension,
    filterParams,
    metricSpecs: [
      { label: 'Day strain', keys: ['day_strain'] },
      { label: 'Active time', keys: ['active_time'] },
      { label: 'Calories burned', keys: ['calories_burn'] },
    ],
  });

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const trendsQuery = useStrainActivityTrends({
    ...filterParams,
    metrics: ['day_strain', 'cardio_load', 'heart_rate', 'calories_burn'],
    granularity,
  });
  const strainPatternsQuery = useStrainPatterns({
    ...filterParams,
    metric: 'strain_patterns',
    granularity: 'weekly',
  });
  const recoveryPatternsQuery = useRecoveryPatterns({
    ...filterParams,
    metric: 'recovery_patterns',
    granularity: 'weekly',
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    const transformed = transformTrendsData(trendsQuery.data, [
      'day_strain',
      'cardio_load',
      'heart_rate',
      'calories_burn',
    ]);
    return transformed.map((item) => {
      const cleaned: Record<string, string | number> = {};
      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (value !== null) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    });
  }, [trendsQuery.data]);

  // ── Comparison overlays ──────────────────────────────────────────────────
  const comparisonTrends = useComparisonTrends(
    groups,
    ['day_strain', 'cardio_load', 'heart_rate', 'calories_burn'],
    granularity,
  );
  const comparisonStrainPatterns = useComparisonPatterns(groups, 'strain_patterns', 'weekly');

  const trendCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonTrends.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, [trendView]),
        })),
        trendView,
        'label',
      ),
    [comparisonTrends, trendView],
  );
  const trendCompareLoading = comparisonTrends.some((e) => e.isLoading);

  const strainPatternCompareChart = useMemo(
    () =>
      buildComparisonPatternSeries(
        comparisonStrainPatterns.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: e.data?.data?.patterns ?? [],
        })),
        'day',
      ),
    [comparisonStrainPatterns],
  );
  const strainPatternCompareLoading = comparisonStrainPatterns.some((e) => e.isLoading);

  const patternsData = useMemo(() => {
    const strainPatterns = strainPatternsQuery.data?.data?.patterns || [];
    const recoveryPatterns = recoveryPatternsQuery.data?.data?.patterns || [];

    if (strainPatterns.length === 0 && recoveryPatterns.length === 0) return [];

    const getPatternLabel = (pattern: PatternDataPoint) => pattern.day ?? pattern.time ?? '';

    const strainMap = new Map(strainPatterns.map((pattern) => [getPatternLabel(pattern), pattern.value]));
    const recoveryMap = new Map(recoveryPatterns.map((pattern) => [getPatternLabel(pattern), pattern.value]));

    const allLabels = new Set([
      ...strainPatterns.map((pattern) => getPatternLabel(pattern)),
      ...recoveryPatterns.map((pattern) => getPatternLabel(pattern)),
    ]);

    return Array.from(allLabels)
      .filter((label) => label)
      .map((label) => ({
        day: label!,
        strain: strainMap.get(label) || 0,
        recovery: recoveryMap.get(label) || 0,
      }));
  }, [strainPatternsQuery.data, recoveryPatternsQuery.data]);

  const hasTrendDataForSelectedMetric = useMemo(
    () =>
      trendsData.some((item) => {
        const value = item[trendView];
        return typeof value === 'number' && Number.isFinite(value);
      }),
    [trendsData, trendView],
  );

  const getTrendLabel = () => {
    switch (trendView) {
      case 'day_strain':
        return 'Day Strain';
      case 'cardio_load':
        return 'Cardio Load';
      case 'heart_rate':
        return 'Heart Rate';
      case 'calories_burn':
        return 'Calories Burned';
      default:
        return 'Day Strain';
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case 'day_strain':
        return 21;
      case 'cardio_load':
        return undefined;
      case 'heart_rate':
        return undefined;
      case 'calories_burn':
        return undefined;
      default:
        return 21;
    }
  };

  const isLoading = trendsQuery.isLoading || strainPatternsQuery.isLoading || recoveryPatternsQuery.isLoading;

  const hasError = trendsQuery.isError || strainPatternsQuery.isError || recoveryPatternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ComparisonToggle comparison={comparison} />
            <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
            <DurationSelector value={dateRange} onChange={handleDateRangeChange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
          dateRange={dateRange}
        />

        {hasError && <Alert severity='error'>Failed to load strain and activity data. Please try again later.</Alert>}

        {/* Stat cards + heart-rate zones, one column per comparison group. */}
        <ComparisonColumns groups={groups} overflowCount={overflowCount}>
          {(group) => (
            <StrainActivityStatsColumn
              filterParams={group.filterParams}
              isCentralAdmin={isCentralAdmin}
              isAnonymised={isAnonymised}
              stacked={isComparing}
            />
          )}
        </ComparisonColumns>

        <DashboardCard title='AI Summary' tooltip='AI narrative of the metrics shown.' expandable={false}>
          <AiSummaryPanel
            title={isComparing ? 'Compare these groups with AI' : 'Summarise this view with AI'}
            buildPayload={buildAiPayload}
            disabled={aiDisabled}
          />
        </DashboardCard>

        <WeatherAnomalyCallout filters={filterParams} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Avg Strain and Activity Trends'
              subtitle='Daily fluctuations in strain levels, highlighting overall exertion and recovery balance.'
              metricKey={trendView}
              details={STRAIN_AND_ACTIVITY_DETAILS.strainAndActivity}
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setTrendView(newValue);
                  }}
                  size='small'
                >
                  <ToggleButton value='day_strain'>Day Strain</ToggleButton>
                  <ToggleButton value='cardio_load'>Cardio Load</ToggleButton>
                  <ToggleButton value='heart_rate'>Heart Rate</ToggleButton>
                  <ToggleButton value='calories_burn'>Calories Burned</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                {(isComparing ? trendCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  trendCompareChart.data.length > 0 ? (
                    <LineChart
                      data={trendCompareChart.data}
                      xAxisKey='label'
                      series={trendCompareChart.series}
                      height={300}
                      yAxisMin={0}
                      yAxisMax={getYAxisMax()}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>
                        No {getTrendLabel().toLowerCase()} data available for the selected filters.
                      </Alert>
                    </Box>
                  )
                ) : trendsData.length > 0 && hasTrendDataForSelectedMetric ? (
                  <AnnotatableChart
                    clientId={effectiveClientId}
                    startDate={eventStartDate}
                    endDate={eventEndDate}
                    weatherFilters={filterParams}
                    canEdit={canEditEvents}
                    data={trendsData}
                    xAxisKey='label'
                    series={[
                      {
                        dataKey: trendView,
                        label: getTrendLabel(),
                        color: theme.palette.warning.main,
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[trendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[trendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>
                      No {getTrendLabel().toLowerCase()} data available for the selected filters.
                    </Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        {/* Heart Rate Zones now renders inside each comparison column
            (StrainActivityStatsColumn). Weekly Strain Patterns overlays one
            strain line per group across the week in comparison mode. */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Weekly Strain Patterns'
              subtitle='Comparison of daily strain and recovery percentages across the week to track workload balance.'
              metricKey='day_strain'
              details={STRAIN_AND_ACTIVITY_DETAILS.weeklyStrainPatterns}
            >
              <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                {(isComparing ? strainPatternCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  strainPatternCompareChart.data.length > 0 ? (
                    <LineChart
                      data={strainPatternCompareChart.data}
                      xAxisKey='day'
                      series={strainPatternCompareChart.series}
                      height={300}
                      yAxisMin={0}
                      yAxisMax={21}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No pattern data available</Alert>
                    </Box>
                  )
                ) : patternsData.length > 0 ? (
                  <CombinedBarLineChart
                    data={patternsData}
                    xAxisKey='day'
                    barSeries={[
                      {
                        dataKey: 'strain',
                        label: 'Day Strain',
                        color: theme.palette.primary.main,
                      },
                    ]}
                    lineSeries={[
                      {
                        dataKey: 'recovery',
                        label: 'Recovery %',
                        color: theme.palette.success.main,
                      },
                    ]}
                    height={300}
                    barYAxisMin={0}
                    barYAxisMax={21}
                    lineYAxisMin={0}
                    lineYAxisMax={100}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
